import React, { useState, useRef, useEffect } from 'react';
import './styles.scss';
import { Box, Card, CardContent, CardHeader, IconButton, Divider, CardActions } from '@mui/material';
import { ExpandMore, ExpandLess, Close } from '@mui/icons-material';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { useHookstate } from '@hookstate/core';
import type { ModalProperties } from './interface';

const isOutOfViewport = (element: HTMLElement): boolean => {
  const rectObject = element.getBoundingClientRect();
  return (
    rectObject.right < 0 ||
    rectObject.bottom < 0 ||
    rectObject.left > window.innerWidth ||
    rectObject.top > window.innerHeight
  );
};

export default function StandardModal({
  dimensions = {},
  avatar: Avatar,
  title,
  subheader,
  position,
  bounds = '#root',
  closeButton,
  isExpanded,
  children,
  handleStop,
  handle = ".standard-modal",
  sx,
  variant = 'body1',
  sxContent,
}: ModalProperties) {
  const [modalIsClosed, setModalIsClosed] = useState(false);
  const draggableRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ x: position.x, y: position.y });

  useEffect(() => {
    const checkPosition = () => {
      if (draggableRef.current && isOutOfViewport(draggableRef.current)) {
        setPos({
          x: window.innerWidth / 2 - (draggableRef.current.offsetWidth / 2),
          y: window.innerHeight / 2 - (draggableRef.current.offsetHeight / 2),
        });
      }
    };

    checkPosition();
    window.addEventListener('resize', checkPosition);
    window.addEventListener('focus', checkPosition);
    return () => {
      window.removeEventListener('resize', checkPosition);
      window.removeEventListener('focus', checkPosition);
    };
  }, []);

  const toggleExpandModal = () => {
    return isExpanded?.set(!isExpanded?.get())
  }
  const closeButtonFunc = () => {
    if (typeof closeButton === 'boolean') {
      setModalIsClosed(true);
    }
    if (typeof closeButton === 'function') {
      closeButton();
    }
  }

  return (
    <Draggable
      nodeRef={draggableRef}
      bounds={bounds}
      handle={handle}
      position={pos}
      onStop={(event: DraggableEvent, data: DraggableData) => {
        setPos({ x: data.x, y: data.y });
        handleStop?.(event, data);
      }}
    >
      <Box ref={draggableRef} sx={{ zIndex: 999, position: 'fixed', display: 'inline-block', ...sx }}>
        <Card>
          <CardHeader
            avatar={
              Avatar ? <Avatar /> : null
            }
            sx={{
              cursor: 'move'
            }}
            className="standard-modal"
            action={
              <CardActions>
                <IconButton onClick={toggleExpandModal} sx={{ zIndex: 9999 }}>
                  {(Boolean(isExpanded?.get())) ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                {Boolean(closeButton) &&
                  <IconButton sx={{ margin: '0 !important' }} onClick={closeButtonFunc} >
                    {<Close />}
                  </IconButton>
                }
              </CardActions>
            }
            title={title}
            subheader={
              subheader ? subheader : ''
            }
            titleTypographyProps={{variant: variant ? variant : 'body1'}}
          />
          <Divider />

          <CardContent
            sx={{
              display: (isExpanded?.get()) ? 'flex' : 'None', ...dimensions,
              "&:last-child": {
                paddingBottom: '16px',
              },
              ...sxContent
            }}
          >
            {children}
          </CardContent>

        </Card>
      </Box>
    </Draggable>
  );
}
